<!-- 分享弹窗 -->
<script lang="ts" setup>
import { computed } from 'vue'
import MoreActionGenerateSimilar from '~/assets/icons/moreActionGenerateSimilar.svg?component'
import { useToastHandler } from '~/composables/useToastHandler'

const { showSuccessToast } = useToastHandler()

interface Props {
  open: boolean
  songTitle?: string
  songUuid?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

// 使用 ref 来管理 Modal 的打开状态
const isOpen = ref(props.open)

// 监听 props.open 变化，同步到内部状态
watch(() => props.open, (newVal) => {
  isOpen.value = newVal
})

// 监听内部状态变化，同步到父组件
watch(isOpen, (newVal) => {
  emit('update:open', newVal)
})

// 计算分享链接
const shareUrl = computed(() => {
  if (!props.songUuid) return ''
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  return `${baseUrl}/songDetail/${props.songUuid}`
})

// 计算分享文本
const shareText = computed(() => {
  const title = props.songTitle || '[xxx]'
  return `Witness what AI can do with music! Check out this 100% AI-generated track "${title}" - it's mind-blowing. 🤯`
})

// 完整的分享内容（文本 + URL）
const fullShareContent = computed(() => {
  return `${shareText.value}\n${shareUrl.value}`
})

// 关闭弹窗
const closeModal = () => {
  emit('update:open', false)
}

// 复制链接
const copyLink = async () => {
  await navigator.clipboard.writeText(fullShareContent.value)
  showSuccessToast('The link has been copied to your clipboard.')
}

// 社交媒体分享
const shareToSocial = (platform: string) => {
  const encodedText = encodeURIComponent(shareText.value)
  const encodedUrl = encodeURIComponent(shareUrl.value)
  const encodedFullText = encodeURIComponent(fullShareContent.value)

  let shareUrl_platform = ''

  switch (platform) {
    case 'twitter':
    case 'x':
      // X (Twitter) 分享
      shareUrl_platform = `https://twitter.com/intent/tweet?text=${encodedFullText}`
      break
    case 'facebook':
      // Facebook 分享
      shareUrl_platform = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`
      break
    case 'reddit':
      // Reddit 分享
      shareUrl_platform = `https://reddit.com/submit?url=${encodedUrl}&title=${encodedText}`
      break
    case 'linkedin':
      // LinkedIn 分享
      shareUrl_platform = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
      break
    case 'telegram':
      // Telegram 分享
      shareUrl_platform = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`
      break
    default:
      return
  }

  if (shareUrl_platform) {
    window.open(shareUrl_platform, '_blank', 'width=600,height=400')
  }
}
</script>

<template>
  <UModal
    v-model:open="isOpen"
    :ui="{
      overlay: 'bg-[#101012B2] backdrop-blur-[3px]',
      content: 'w-[calc(100%-32px)] md:w-[800px] max-w-max bg-[#2D2D39] rounded-[16px] ring-[none]'
    }"
  >
    <template #content>
      <div class="relative flex flex-col gap-y-[24px] w-full h-full font-['Roboto'] p-[32px]">
        <!-- 关闭按钮 -->
        <div
          class="absolute top-[16px] right-[16px] flex items-center justify-center w-[34px] h-[34px] cursor-pointer before:content-[''] before:absolute before:w-[20px] before:h-[2px] before:bg-[#8888A1] before:rotate-45 before:rounded-[20px] after:content-[''] after:absolute after:w-[20px] after:h-[2px] after:bg-[#8888A1] after:-rotate-45 after:rounded-[20px] hover:before:bg-[#E7E7EC] hover:after:bg-[#E7E7EC] transition-colors"
          @click="closeModal"
        />

        <!-- 标题 -->
        <div class="flex items-center justify-center">
          <h2 class="text-[24px] text-[#FFFFFF] leading-[28px] font-bold">
            Share
          </h2>
        </div>

        <div class="flex items-center text-[16px] text-white font-bold">
          <MoreActionGenerateSimilar class="size-[24px] p-[4px]" />
          <span>Copy link</span>
        </div>

        <!-- 分享内容框 -->
        <div class="flex justify-between text-[14px] text-[#E7E7EC] leading-[20px] bg-[#1A1A24] border border-[#5E5E77] rounded-[8px] p-[12px]">
          <div class="flex flex-col">
            <p class="whitespace-pre-wrap">
              {{ shareText }}
            </p>
            <p class="break-all">
              {{ shareUrl }}
            </p>
          </div>
          <button
            class="flex items-center gap-x-[8px] h-[32px] px-[12px] bg-[#383847] hover:bg-[#46465D] text-[14px] text-[#E7E7EC] rounded-[8px] transition-colors cursor-pointer"
            @click="copyLink"
          >
            <MoreActionGenerateSimilar class="size-[16px]" />
            <span>Copy</span>
          </button>
        </div>

        <!-- 社交媒体分享图标 -->
        <div class="flex flex-wrap items-center justify-center gap-[24px]">
          <!-- X (Twitter) -->
          <button
            class="flex flex-col items-center gap-y-[8px] cursor-pointer group"
            @click="shareToSocial('x')"
          >
            <div class="flex items-center justify-center size-[48px] bg-[#383847] rounded-full group-hover:bg-[#46465D] transition-colors">
              <svg
                class="size-[24px] text-[#E7E7EC]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                />
              </svg>
            </div>
            <span class="text-[12px] text-[#8888A1] group-hover:text-[#E7E7EC] transition-colors">
              X
            </span>
          </button>

          <!-- Facebook -->
          <button
            class="flex flex-col items-center gap-y-[8px] cursor-pointer group"
            @click="shareToSocial('facebook')"
          >
            <div class="flex items-center justify-center size-[48px] bg-[#383847] rounded-full group-hover:bg-[#46465D] transition-colors">
              <svg
                class="size-[24px] text-[#E7E7EC]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                />
              </svg>
            </div>
            <span class="text-[12px] text-[#8888A1] group-hover:text-[#E7E7EC] transition-colors">
              Facebook
            </span>
          </button>

          <!-- Reddit -->
          <button
            class="flex flex-col items-center gap-y-[8px] cursor-pointer group"
            @click="shareToSocial('reddit')"
          >
            <div class="flex items-center justify-center size-[48px] bg-[#383847] rounded-full group-hover:bg-[#46465D] transition-colors">
              <svg
                class="size-[24px] text-[#E7E7EC]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"
                />
              </svg>
            </div>
            <span class="text-[12px] text-[#8888A1] group-hover:text-[#E7E7EC] transition-colors">
              Reddit
            </span>
          </button>

          <!-- LinkedIn -->
          <button
            class="flex flex-col items-center gap-y-[8px] cursor-pointer group"
            @click="shareToSocial('linkedin')"
          >
            <div class="flex items-center justify-center size-[48px] bg-[#383847] rounded-full group-hover:bg-[#46465D] transition-colors">
              <svg
                class="size-[24px] text-[#E7E7EC]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                />
              </svg>
            </div>
            <span class="text-[12px] text-[#8888A1] group-hover:text-[#E7E7EC] transition-colors">
              Linkedin
            </span>
          </button>

          <!-- Telegram -->
          <button
            class="flex flex-col items-center gap-y-[8px] cursor-pointer group"
            @click="shareToSocial('telegram')"
          >
            <div class="flex items-center justify-center size-[48px] bg-[#383847] rounded-full group-hover:bg-[#46465D] transition-colors">
              <svg
                class="size-[24px] text-[#E7E7EC]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"
                />
              </svg>
            </div>
            <span class="text-[12px] text-[#8888A1] group-hover:text-[#E7E7EC] transition-colors">
              Facebook
            </span>
          </button>
        </div>
      </div>
    </template>
  </UModal>
</template>
